import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Typography, MenuItem } from "@mui/material";
import { setProfile, startSprint, stopSprint } from "../store/personalSlice";
import { calcDailyCalories } from "../utils/calcCalories";
import { calcTargetCalories } from "../utils/calcTargetCalories";

import { colors, space, radius, borders } from "../design/tokens";

import {
  Screen,
  Content,
  Card,
  TextInput,
  Select,
  Button,
  Text
} from "../components/layout";

function validate(values) {
  const e = {};

  if (!values.heightCm || values.heightCm < 120 || values.heightCm > 230)
    e.heightCm = "Height must be 120–230 cm";

  if (!values.baseWeight || values.baseWeight < 30 || values.baseWeight > 200)
    e.baseWeight = "Weight must be 30–200 kg";

  if (
    !values.birthYear ||
    values.birthYear < 1900 ||
    values.birthYear > new Date().getFullYear()
  )
    e.birthYear = "Birthyear must be greater than 1900";

  if (values.targetWeight && values.baseWeight && values.targetWeight >= values.baseWeight)
    e.targetWeight = "Target must be lower";

  if (values.timeSpan && (values.timeSpan < 7 || values.timeSpan > 365))
    e.timeSpan = "7–365 days";

  return e;
}

export default function MePage() {
  const dispatch = useDispatch();

  const profile = useSelector((state) => state.personal.profile);
  const sprint = useSelector((state) => state.personal.sprint);
  const byDate = useSelector((state) => state.personal?.byDate || {});

  const today = new Date().toISOString().split("T")[0];
  const todayWeight = useSelector((state) => state.personal.byDate[today]?.weight);
  const weightForCalc = todayWeight || profile.baseWeight;

  // --- Sprint local state stays as-is (not profile fields) ---
  const [days, setDays] = React.useState(7);
  const [extraLow, setExtraLow] = React.useState(20);
  const [extraHigh, setExtraHigh] = React.useState(10);
  const [sprintTarget, setSprintTarget] = React.useState("");

  // --- RHF setup ---
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    watch,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: profile,
    mode: "onChange",
  });

  // Sync profile from Redux into form (e.g. on load or external update)
  useEffect(() => {
    reset(profile);
  }, [profile, reset]);

  // Watch values needed for derived calculations
  const watchedValues = watch();
  const calories = calcDailyCalories(watchedValues, weightForCalc);
  const targetCalories = calcTargetCalories(watchedValues, weightForCalc, sprint, watchedValues.timeSpan);
  const bmi =
    watchedValues.heightCm && weightForCalc
      ? (weightForCalc / (watchedValues.heightCm / 100) ** 2).toFixed(1)
      : "";

  // Sprint target suggestion
  useEffect(() => {
    if (!weightForCalc) return;
    const lossPerDay = 1 / 7;
    let suggested = weightForCalc - lossPerDay * days;
    if (watchedValues.targetWeight) suggested = Math.max(watchedValues.targetWeight, suggested);
    setSprintTarget(Number(suggested.toFixed(1)));
  }, [days, weightForCalc, watchedValues.targetWeight]);

  function getEndDate(days) {
    const d = new Date();
    d.setDate(d.getDate() + Number(days));
    return d.toISOString().split("T")[0];
  }

  // --- Submit ---
  const onSubmit = (data) => dispatch(setProfile(data));

  // --- Reusable Controller field ---
  const field = (name, label ) => (
    <Controller
      key={name}
      control={control}
      name={name}
      rules={{
        validate: (value) => {
          const errs = validate({ ...getValues(), [name]: value });
          return errs[name] || true;
        },
      }}
      render={({ field, fieldState }) => (
        <TextInput
          label={label}
          error={fieldState.error?.message}
          // placeholder={label}
          value={field.value ?? ""}
          onBlur={field.onBlur}
          ref={field.ref}
          onChange={(e) => field.onChange(Number(e.target.value))}
          style={{
            background: colors.surfaceSoft,
            borderBottom: borders.sm,
            borderColor: fieldState.error ? "red" : colors.primary,
            color: fieldState.error ? "red" : colors.text,
          }}
        />
      )}
    />
  );

  return (
    <Screen>
      <Content>
        <Card style={{ padding: space.sm, gap: space.lg }}>
          <Text variant="h2" sx={{ mt: 4 }}>
            Me
          </Text>


          {field("heightCm", "Height (cm)", "Bitte Grösse eingeben")}
          {field("baseWeight", "Starting Weight (kg)", "Bitte Gewicht eingeben")}
          {field("targetWeight", "Target weight (kg)", "Bitte Ziel-Gewicht eingeben")}
          {field("birthYear", "Birth Year", "Bitte Geburtsjahr eingeben")}
          {field("timeSpan", "Time span (days)", "Bitte Zeitraum eingeben")}

          <Controller
            control={control}
            name="jobType"
            render={({ field }) => (
              <Select
                value={field.value || "office"}
                placeholder="Job"
                onChange={(value) => field.onChange(value)}
                options={[
                  { label: "Office / Bureau", value: "office" },
                  { label: "Light work", value: "light" },
                  { label: "Medium work", value: "medium" },
                  { label: "Heavy physical work", value: "heavy" },
                ]}
              />
            )}
          />

          <Button

            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
          >
            Save Profile
          </Button>

          <Text variant="h3">BMI: {bmi || "--"}</Text>
          <Text variant="h3">Daily calories: {calories || "--"} kcal</Text>
          <Text variant="h3">Target calories: {targetCalories || "--"} kcal</Text>

          <Text variant="h2">
            Sprint Mode
          </Text>

          {!sprint && (
            <>
              <Select
                value={days}
                placeholder="Sprint duration"
                onChange={(value) => setDays(Number(value))}
                options={[
                  { label: "3 days", value: 3 },
                  { label: "5 days", value: 5 },
                  { label: "7 days", value: 7 },
                  { label: "10 days", value: 10 },
                  { label: "14 days", value: 14 },
                ]}
              />

              <TextInput
                placeholder="Sprint target weight"
                value={sprintTarget}
                onChange={(e) => setSprintTarget(e.target.value)}
              />

              <TextInput
                placeholder="Extra light activity (min/day)"
                value={extraLow}
                onChange={(e) => setExtraLow(e.target.value)}
              />

              <TextInput
                placeholder="Extra heavy activity (min/day)"
                value={extraHigh}
                onChange={(e) => setExtraHigh(e.target.value)}
                style={{
                  background: colors.surfaceSoft,
                  borderColor: colors.primary,
                  color: colors.text,
                }}
              />

              <Button

                onClick={() =>
                  dispatch(
                    startSprint({
                      startDate: today,
                      endDate: getEndDate(days),
                      startWeight: weightForCalc,
                      targetWeight: Number(sprintTarget),
                      extraLowMin: Number(extraLow),
                      extraHighMin: Number(extraHigh),
                    })
                  )
                }
              >
                Start Sprint
              </Button>
            </>
          )}

          {sprint && (
            <>
              <Text >Sprint active until {sprint.endDate}</Text>
              <Text>Goal: {sprint.targetWeight} kg</Text>
              <Text>Start: {sprint.startWeight} kg</Text>
              <Text>Current: {weightForCalc || "--"} kg</Text>

              <Button
                onClick={() => dispatch(stopSprint())}
              >
                Stop Sprint
              </Button>
            </>
          )}
        </Card>
      </Content>
    </Screen>
  );
}
